import { useState } from "react";

import PropTypes from "prop-types";
import { Controller, useForm } from "react-hook-form";

import { yupResolver } from "@hookform/resolvers/yup";
import { Button, Dialog, DialogActions, DialogContent, Stack } from "@mui/material";

import { apiOutletClient, getErrorMessage } from "../../shared";
import { CustomDialogTitle, CustomInputField, CustomSelect } from "../../ui";
import { useSnackbar } from "../../ui";
import {
  DEFAULT_VALUES,
  VALIDATION_SCHEMA,
} from "./sales-order-void-confirmation-dialog-constants";

export const SalesOrderVoidConfirmationDialog = ({
  referenceNumber,
  onClose,
  onSubmitted,
}) => {
  const { showSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState(false);
  const {
    register,
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: DEFAULT_VALUES,
    resolver: yupResolver(VALIDATION_SCHEMA),
  });

  const preservedReason = watch("preservedReason");

  const voidSalesOrder = async ({ preservedReason, reason }) => {
    const finalReason = preservedReason === "Others" ? reason : preservedReason;

    try {
      setIsLoading(true);
      await apiOutletClient.patch(`sales-orders/${referenceNumber}/void`, {
        reason: finalReason,
      });
      onSubmitted();
      onClose();
    } catch (error) {
      showSnackbar(
        getErrorMessage(error)
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog
      open
      fullWidth
      component="form"
      noValidate
      onClose={onClose}
      onSubmit={handleSubmit(voidSalesOrder)}
    >
      <CustomDialogTitle title="Konfirmasi Void Penjualan" onClose={onClose} />
      <DialogContent>
        <Stack spacing={2}>
          <Controller
            name="preservedReason"
            control={control}
            render={({ field, fieldState }) => (
              <CustomSelect
                required
                id="preservedReason"
                label="Alasan"
                options={[
                  {
                    value: "Customer minta ganti ukuran lensa",
                    label: "Customer minta ganti ukuran lensa",
                  },
                  {
                    value: "⁠Salah input ukuran lensa",
                    label: "⁠Salah input ukuran lensa",
                  },
                  {
                    value: "⁠Salah input jenis lensa",
                    label: "⁠Salah input jenis lensa",
                  },
                  {
                    value: "Salah input produk umum",
                    label: "Salah input produk umum",
                  },
                  {
                    value: "⁠⁠⁠Salah metode pembayaran",
                    label: "⁠⁠⁠Salah metode pembayaran",
                  },
                  {
                    value: "⁠⁠⁠Upgrade penjualan",
                    label: "⁠⁠⁠Upgrade penjualan",
                  },
                  { value: "Others", label: "Lainnya" },
                ]}
                value={field.value}
                onChange={field.onChange}
                error={fieldState.invalid}
                helperText={fieldState.error?.message}
              />
            )}
          />
          {preservedReason === "Others" && (
            <CustomInputField
              required
              name="reason"
              label="Alasan Lainnya"
              register={register}
              InputProps={{ multiline: true }}
              helperText={errors.reason?.message}
              error={Boolean(errors.reason)}
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          loading={isLoading}
          variant="contained"
          color="error"
          type="submit"
        >
          Void
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SalesOrderVoidConfirmationDialog;

SalesOrderVoidConfirmationDialog.propTypes = {
  referenceNumber: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmitted: PropTypes.func.isRequired,
};
